import React, { useState } from 'react';
import Footer from '../components/Footer';
import { Send, GraduationCap, Clock3, Globe, Star, CheckCircle2 } from 'lucide-react';
import { supabase } from '../supabase';

const JOB = {
  id: 'writer-001',
  title: 'Academic Writer',
  type: 'Freelance / Remote',
  level: 'Open to All Levels',
  location: 'Remote — Worldwide',
  posted: 'July 2025',
  perks: ['Flexible hours — work any time', 'Competitive pay per page/word', 'Weekly payments via PayPal or Bank Transfer', 'Ongoing orders — consistent work', 'Supportive team & quality feedback'],
  requirements: [
    "Bachelor's, Master's, or PhD degree in any academic field",
    'Strong academic writing and research skills',
    'Ability to meet deadlines consistently',
    'Attention to detail and proper referencing (APA, Harvard, MLA etc.)',
    'Good command of English (other languages a bonus)',
    'Access to academic databases and research tools (preferred)',
  ],
  responsibilities: [
    'Write original, plagiarism-free academic papers, essays, dissertations, and research projects',
    'Follow client briefs, rubrics, and specific academic standards',
    'Complete tasks within agreed deadlines',
    'Conduct proper literature reviews and cite sources correctly',
    'Accept and incorporate quality feedback from our QA team',
    'Communicate clearly with the team regarding order progress',
  ],
  subjects: ['Business & Management', 'Nursing & Healthcare', 'Law', 'Psychology', 'Engineering', 'Computer Science', 'Finance & Economics', 'Education', 'Social Sciences', 'Any other academic discipline'],
};

function Chip({ text }) {
  return (
    <span style={{display:'inline-block',background:'rgba(15,82,186,0.08)',color:'#0F52BA',fontSize:12,fontWeight:600,padding:'4px 12px',borderRadius:20,margin:'3px'}}>
      {text}
    </span>
  );
}

export default function CareersPage({ navigate }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', phone:'', subject:'', degree:'', cover:'' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = k => e => { setError(''); setForm(f => ({ ...f, [k]: e.target.value })); };

  const handleApply = async () => {
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.cover.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setSaving(true);
    try {
      // Store application as a contact message
      const { error: err } = await supabase.from('contact_messages').insert({
        name:    form.name.trim(),
        email:   form.email.trim(),
        subject: `Writer Application — ${form.subject}`,
        message: `WRITER APPLICATION\n\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nDegree/Qualification: ${form.degree}\nSubject Speciality: ${form.subject}\n\nCover Letter:\n${form.cover}`,
      });
      if (err) throw err;
      setDone(true);
    } catch(e) {
      setError(`Application failed: ${e.message}. Please email us directly at info@arkexpertresearch.com`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-hero">
        <div className="section-label">Join Our Team</div>
        <h1>Careers at Ark Expert Researchers</h1>
        <p style={{maxWidth:540,margin:'0 auto'}}>
          Help students worldwide succeed academically. Work flexibly, earn competitively, and grow with a team that values expertise.
        </p>
      </div>

      <section style={{padding:'56px 5%',maxWidth:900,margin:'0 auto'}}>

        {/* Why join us */}
        <div style={{marginBottom:52,textAlign:'center'}}>
          <div className="section-label text-center">Why Work With Us</div>
          <h2 className="section-title text-center">Benefits of Joining Ark</h2>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:16,marginTop:28}}>
            {[
              { Icon:Clock3,         color:'#0F52BA', title:'Flexible Schedule',   desc:'Work whenever suits you — day, evening, or weekends. Full control over your time.' },
              { Icon:Globe,          color:'#16a34a', title:'100% Remote',         desc:'Work from anywhere in the world. All you need is internet access.' },
              { Icon:Star,           color:'#FF7A00', title:'Competitive Pay',     desc:'Earn per page or per word. Rates increase with performance and experience.' },
              { Icon:GraduationCap,  color:'#7C3AED', title:'All Subjects Welcome',desc:'From nursing to engineering to law — we need experts across every discipline.' },
            ].map(({ Icon, color, title, desc }) => (
              <div key={title} style={{background:'#fff',border:'1px solid var(--border)',borderRadius:14,padding:24,textAlign:'center'}}>
                <div style={{width:48,height:48,borderRadius:12,background:`${color}15`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px',color}}>
                  <Icon size={24}/>
                </div>
                <div style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:14,color:'#0A1931',marginBottom:6}}>{title}</div>
                <div style={{fontSize:13,color:'var(--text-muted)',lineHeight:1.6}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Job post */}
        <div style={{background:'#fff',border:'1px solid var(--border)',borderRadius:18,overflow:'hidden',marginBottom:40}}>
          {/* Job header */}
          <div style={{background:'linear-gradient(135deg,#0A1931,#0F52BA)',padding:'28px 28px 24px'}}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>Now Hiring</div>
                <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'1.5rem',fontWeight:800,color:'#fff',margin:'0 0 8px'}}>{JOB.title}</h2>
                <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                  {[JOB.type, JOB.location, JOB.level].map(t => (
                    <span key={t} style={{fontSize:12,fontWeight:600,color:'rgba(255,255,255,0.75)',display:'flex',alignItems:'center',gap:4}}>● {t}</span>
                  ))}
                </div>
              </div>
              <button
                className="btn-primary"
                style={{background:'#FF7A00',border:'none',padding:'12px 24px',flexShrink:0,width:'100%',maxWidth:200,justifyContent:'center'}}
                onClick={() => { setOpen(true); document.getElementById('apply-form')?.scrollIntoView({behavior:'smooth'}); }}>
                Apply Now →
              </button>
            </div>
          </div>

          {/* Job details */}
          <div style={{padding:'clamp(16px,4vw,28px)'}}>
            {/* Perks */}
            <div style={{marginBottom:24}}>
              <h3 style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:15,marginBottom:12}}>What We Offer</h3>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {JOB.perks.map((p,i) => (
                  <div key={i} style={{display:'flex',alignItems:'flex-start',gap:8}}>
                    <CheckCircle2 size={16} color="#16a34a" style={{flexShrink:0,marginTop:2}}/>
                    <span style={{fontSize:14,color:'#374151'}}>{p}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div style={{marginBottom:24}}>
              <h3 style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:15,marginBottom:12}}>Requirements</h3>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {JOB.requirements.map((r,i) => (
                  <div key={i} style={{display:'flex',alignItems:'flex-start',gap:8}}>
                    <span style={{color:'#0F52BA',fontWeight:700,flexShrink:0}}>→</span>
                    <span style={{fontSize:14,color:'#374151'}}>{r}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Responsibilities */}
            <div style={{marginBottom:24}}>
              <h3 style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:15,marginBottom:12}}>Responsibilities</h3>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {JOB.responsibilities.map((r,i) => (
                  <div key={i} style={{display:'flex',alignItems:'flex-start',gap:8}}>
                    <span style={{color:'#FF7A00',fontWeight:700,flexShrink:0}}>·</span>
                    <span style={{fontSize:14,color:'#374151'}}>{r}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Subjects */}
            <div>
              <h3 style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:15,marginBottom:10}}>Subject Areas (not limited to)</h3>
              <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                {JOB.subjects.map(s => <Chip key={s} text={s}/>)}
              </div>
            </div>
          </div>
        </div>

        {/* Application form */}
        <div id="apply-form" style={{background:'var(--gray)',borderRadius:18,padding:'clamp(24px,5vw,40px)'}}>
          <div style={{textAlign:'center',marginBottom:28}}>
            <div className="section-label text-center">Apply Now</div>
            <h2 className="section-title text-center" style={{fontSize:'clamp(1.2rem,4vw,1.6rem)'}}>Submit Your Application</h2>
            <p style={{color:'var(--text-muted)',fontSize:14,maxWidth:480,margin:'0 auto'}}>
              Fill in the form below and we'll get back to you within 48 hours.
            </p>
          </div>

          {done ? (
            <div style={{textAlign:'center',padding:'32px 16px'}}>
              <CheckCircle2 size={56} color="#16a34a" style={{marginBottom:16}}/>
              <h3 style={{fontFamily:'Sora,sans-serif',fontWeight:700,marginBottom:8}}>Application Received!</h3>
              <p style={{color:'var(--text-muted)',fontSize:14,marginBottom:20}}>
                Thank you for applying. Our team will review your application and get back to you as soon as possible.
              </p>
              <button className="btn-primary" onClick={() => { setDone(false); setForm({ name:'', email:'', phone:'', subject:'', degree:'', cover:'' }); }}>
                Submit Another
              </button>
            </div>
          ) : (
            <div style={{maxWidth:620,margin:'0 auto',display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,220px),1fr))',gap:14}}>
              <div className="form-group" style={{margin:0}}>
                <label>Full Name *</label>
                <input type="text" placeholder="Your full name" value={form.name} onChange={set('name')} style={{fontSize:16}}/>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Email Address *</label>
                <input type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} style={{fontSize:16}}/>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Phone / WhatsApp</label>
                <input type="tel" placeholder="+1 234 567 8900" value={form.phone} onChange={set('phone')} style={{fontSize:16}}/>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label>Degree / Qualification *</label>
                <input type="text" placeholder="e.g. PhD Finance, MSc Nursing" value={form.degree} onChange={set('degree')} style={{fontSize:16}}/>
              </div>
              <div className="form-group" style={{margin:0,gridColumn:'1 / -1'}}>
                <label>Subject Speciality *</label>
                <input type="text" placeholder="e.g. Business, Nursing, Law, Engineering" value={form.subject} onChange={set('subject')} style={{fontSize:16}}/>
              </div>
              <div className="form-group" style={{margin:0,gridColumn:'1 / -1'}}>
                <label>Cover Letter / Why You Want to Join *</label>
                <textarea
                  placeholder="Tell us about your academic background, writing experience, and why you'd like to join Ark Expert Researchers..."
                  value={form.cover} onChange={set('cover')} rows={5} style={{fontSize:16,resize:'vertical',minHeight:120}}
                />
              </div>
              {error && (
                <div style={{gridColumn:'span 2',background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:8,padding:'10px 14px',color:'#DC2626',fontSize:13}}>
                  ⚠️ {error}
                </div>
              )}
              <div style={{gridColumn:'1 / -1',textAlign:'center'}}>
                <button
                  className="btn-primary"
                  style={{padding:'13px 36px',fontSize:15,opacity:saving?0.7:1,width:'100%',maxWidth:320,justifyContent:'center'}}
                  onClick={handleApply} disabled={saving}>
                  <Send size={16}/> {saving ? 'Submitting…' : 'Submit Application'}
                </button>
                <p style={{fontSize:12,color:'var(--text-muted)',marginTop:10}}>
                  Or email your CV directly to <strong>info@arkexpertresearch.com</strong>
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer navigate={navigate}/>
    </div>
  );
}
